'use strict';

/**
 * @fileoverview The ONE deep freeze (MUTATION-INSTRUMENT-001 F-TOCONTAIN-2).
 *
 * Eight hand-rolled copies preceded it and their semantics had drifted apart: five skipped any value
 * `Object.isFrozen` already reported (so a frozen parent's mutable child was never reached), two
 * recursed forever on a cycle, one special-cased Joi schemas by name, one copied instead of freezing
 * in place, and every one of them "froze" a Set or Map it met — which freezes nothing, because a
 * collection's contents live in an internal slot. This module fixes one semantics for all of them:
 *
 * - **In place.** The argument is returned, frozen. Identity is preserved.
 * - **The plain-data tree.** Plain objects (prototype `Object.prototype` or `null`) and arrays are
 *   frozen, and EVERY own data property is descended — symbol-keyed and non-enumerable ones too,
 *   which `Object.values` never sees. An accessor is frozen in place and its getter is never invoked.
 * - **Through already-frozen parents.** A frozen object can hold a mutable child; frozenness of a
 *   node says nothing about its children.
 * - **Cycle-safe.** Each object is visited once.
 * - **Class instances are behaviour, not data.** An object with any other prototype (a Joi schema
 *   with its internal caches, a RegExp whose `/g` state lives in `lastIndex`, a service) is neither
 *   frozen nor descended — freezing it would break it, and its mutability is its class's contract.
 *   Functions are left alone for the same reason.
 * - **Read-only collections are walked.** A `ReadOnlySet` / `ReadOnlyMap` is already immutable; its
 *   object members, keys and values are frozen like any other child.
 * - **Refusal, not a false promise.** A `Set`, `Map`, `WeakSet`, `WeakMap` or `Date` anywhere in the
 *   tree throws a TypeError naming its path, because freezing it would leave its contents writable.
 *   The brand is detected by its internal slot, not by its name, so a subclass is caught and a
 *   spoofed `Symbol.toStringTag` is not mistaken for one.
 * - **Atomic.** The whole tree is walked before anything is frozen, so a refusal leaves the argument
 *   exactly as it was.
 *
 * @module @puffin-core/read-only/deepFreeze
 */

const { ReadOnlySet } = require('./readOnlySet');
const { ReadOnlyMap } = require('./readOnlyMap');

/**
 * The built-ins whose state `Object.freeze` cannot reach, each with a brand check that throws on
 * any receiver lacking the internal slot, and the advice the refusal carries.
 */
const SLOT_BACKED = Object.freeze([
  Object.freeze({ brand: 'Set', probe: Set.prototype.has, advice: 'hold it in a ReadOnlySet' }),
  Object.freeze({ brand: 'Map', probe: Map.prototype.has, advice: 'hold it in a ReadOnlyMap' }),
  Object.freeze({
    brand: 'WeakSet',
    probe: WeakSet.prototype.has,
    advice: 'a weak collection cannot be read-only',
  }),
  Object.freeze({
    brand: 'WeakMap',
    probe: WeakMap.prototype.has,
    advice: 'a weak collection cannot be read-only',
  }),
  Object.freeze({
    brand: 'Date',
    probe: Date.prototype.getTime,
    advice: 'store an ISO string or an epoch number',
  }),
]);

const IDENTIFIER = /^[A-Za-z_$][\w$]*$/;

/**
 * @param {*} value
 * @returns {{brand: string, advice: string}|undefined} The slot-backed built-in `value` is, if any.
 */
function slotBackedBrand(value) {
  return SLOT_BACKED.find(({ probe }) => {
    try {
      Reflect.apply(probe, value, []);
      return true;
    } catch {
      return false;
    }
  });
}

/**
 * @param {Object} value
 * @returns {boolean} Whether `value` is plain data: an array, or an object whose prototype is
 *   `Object.prototype` or `null`.
 */
function isPlainData(value) {
  if (Array.isArray(value)) return true;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/**
 * @param {string} path - The parent's path.
 * @param {string|symbol} key
 * @param {boolean} inArray - Whether the parent is an array.
 * @returns {string} The child's path, `$.a.b[0]` style.
 */
function childPath(path, key, inArray) {
  if (typeof key === 'symbol') return `${path}[${String(key)}]`;
  if (inArray && /^\d+$/.test(key)) return `${path}[${key}]`;
  return IDENTIFIER.test(key) ? `${path}.${key}` : `${path}[${JSON.stringify(key)}]`;
}

/**
 * @param {*} key - A ReadOnlyMap key.
 * @param {number} index - Its insertion position.
 * @returns {string} How the key reads inside `.get(…)` in a path.
 */
function mapKeyLabel(key, index) {
  if (typeof key === 'string') return JSON.stringify(key);
  if (key !== null && (typeof key === 'object' || typeof key === 'function')) {
    return `<key ${index}>`;
  }
  return String(key);
}

/**
 * Walk `value`, collecting every plain-data node to freeze; throw on a slot-backed built-in.
 *
 * @param {*} value
 * @param {string} path
 * @param {WeakSet<Object>} seen
 * @param {Array<Object>} toFreeze
 */
function collect(value, path, seen, toFreeze) {
  if (value === null || typeof value !== 'object' || seen.has(value)) return;
  seen.add(value);

  if (value instanceof ReadOnlySet) {
    let index = 0;
    for (const member of value) {
      collect(member, `${path}<member ${index}>`, seen, toFreeze);
      index += 1;
    }
    return;
  }
  if (value instanceof ReadOnlyMap) {
    let index = 0;
    for (const [key, item] of value) {
      collect(key, `${path}<key ${index}>`, seen, toFreeze);
      collect(item, `${path}.get(${mapKeyLabel(key, index)})`, seen, toFreeze);
      index += 1;
    }
    return;
  }

  const slotBacked = slotBackedBrand(value);
  if (slotBacked) {
    throw new TypeError(
      `deepFreeze: ${path} is a ${slotBacked.brand}, whose contents Object.freeze cannot reach — ${slotBacked.advice}`,
    );
  }
  if (!isPlainData(value)) return;

  toFreeze.push(value);
  const inArray = Array.isArray(value);
  for (const key of Reflect.ownKeys(value)) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if ('value' in descriptor) {
      collect(descriptor.value, childPath(path, key, inArray), seen, toFreeze);
    }
  }
}

/**
 * Deep-freeze a plain-data tree in place (see the module docblock for the exact semantics).
 *
 * @template T
 * @param {T} value
 * @returns {T} `value`, with every plain object and array in it frozen.
 * @throws {TypeError} When the tree holds a Set, Map, WeakSet, WeakMap or Date — nothing is frozen.
 */
function deepFreeze(value) {
  const toFreeze = [];
  collect(value, '$', new WeakSet(), toFreeze);
  for (const node of toFreeze) Object.freeze(node);
  return value;
}

module.exports = { deepFreeze };
